import { Spinner } from 'components/spinner/Spinner';
import type * as CSS from 'csstype';
import { usePicture } from 'custom_hooks/data_hooks/picture/usePicture';
import { useEffect } from 'react';
import * as React from 'react';

type PropsFromImage = 'src' | 'alt' | 'className' | 'aria-label';
type ImageProps = React.DetailedHTMLProps<
    React.ImgHTMLAttributes<HTMLImageElement>,
    HTMLImageElement
>;
export type AuthorizedImageProps = Pick<ImageProps, PropsFromImage> & {
    /**
     * Displayed while the image is being loaded.
     */
    pending?: React.ReactNode;
    /**
     * Displayed when there is an error fetching the image or the image source is undefined. Defaults to nothing.
     */
    fallback?: React.ReactNode;

    /**
     * How to scale the image ('object-fit' css property)
     */
    imageScaling?: CSS.Property.ObjectFit;
};

export function AuthorizedImage({
    src,
    pending,
    fallback,
    alt,
    className,
    'aria-label': ariaLabel,
    imageScaling,
}: AuthorizedImageProps) {
    const {
        data: image,
        refetch: getImageBlob,
        error,
        isLoading: picturePending,
    } = usePicture(src);

    useEffect(() => {
        const fetchImageWhenSourceChanges = async () => {
            if (src) {
                await getImageBlob();
            }
        };
        fetchImageWhenSourceChanges();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src]);

    if (picturePending) {
        return pending ? <>{pending}</> : <Spinner />;
    }

    if (error || !image) {
        return fallback ? <>{fallback}</> : <></>;
    }

    return (
        <img
            src={image}
            alt={alt}
            className={className}
            aria-label={ariaLabel}
            style={{ objectFit: imageScaling }}
        />
    );
}
